import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Scale, ShieldCheck } from "lucide-react";

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-start items-center">
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8 sm:h-9 sm:w-9" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary via-red-700 to-primary bg-clip-text text-transparent group-hover:from-red-800 group-hover:via-primary group-hover:to-red-800 transition-all duration-300">
              VetriaAI
            </span>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-8 sm:py-12 md:py-16 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-blue-50 opacity-50" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(185,28,28,0.1),transparent_50%)]" />

        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-full mb-4 sm:mb-6">
            <FileText className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Terms & Conditions
          </h1>
          <p className="text-sm sm:text-base text-gray-600">Last Updated: October 9, 2025</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-8 sm:py-12 bg-gradient-to-b from-white to-gray-50 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-card shadow-card border border-gray-100 p-6 sm:p-8 md:p-10">
            <div className="space-y-8 text-gray-700 leading-relaxed">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-primary mt-1" />
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Welcome</h2>
                  <p className="text-sm sm:text-base">
                    By accessing or using VetriaAI, you agree to these Terms & Conditions. If you are
                    using the Service on behalf of an organization, you represent that you have
                    authority to bind that organization.
                  </p>
                </div>
              </div>

              <div className="grid gap-6 sm:gap-8">
                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <Scale className="w-5 h-5 text-primary" />
                    1) Definitions
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                    <li><strong>VetriaAI / we / us / our:</strong> VETRIAAI INNOVATION PRIVATE LIMITED.</li>
                    <li><strong>Service:</strong> Web apps, APIs, assessments, analytics, and related materials.</li>
                    <li><strong>Customer:</strong> Organization or individual purchasing a plan.</li>
                    <li><strong>User:</strong> Anyone with an account (admins, recruiters, candidates).</li>
                    <li><strong>Content:</strong> Data you upload or generate (job descriptions, resumes, results, badges).</li>
                  </ul>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">2) Account & Eligibility</h3>
                  <p className="text-sm sm:text-base">
                    Provide accurate information and keep credentials secure. You are responsible for all
                    activity under your account.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">3) Acceptable Use</h3>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                    <li>Do not upload unlawful, discriminatory, or harmful content.</li>
                    <li>Do not bypass security, misuse APIs, or reverse engineer at scale.</li>
                    <li>Do not use results to unlawfully discriminate or violate labor laws.</li>
                  </ul>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">4) AI Outputs & Human Oversight</h3>
                  <p className="text-sm sm:text-base">
                    AI assessments and rankings are advisory. Human review is required. You remain
                    responsible for hiring decisions and compliance with employment and anti-discrimination
                    laws.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">5) Data & Privacy</h3>
                  <p className="text-sm sm:text-base">
                    We process data per our Privacy Policy and any Data Processing Addendum. You confirm a
                    lawful basis (consent or legitimate interests) for processing candidate data.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">6) Security & Compliance</h3>
                  <p className="text-sm sm:text-base">
                    We implement encryption, MFA, RBAC, audit logs, backups, and follow standards such as
                    SOC 2 Type II, ISO 27001, GDPR, and CCPA (see Privacy Policy for details).
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">7) Integrations & Third Parties</h3>
                  <p className="text-sm sm:text-base">
                    Integrations (ATS/CRM/communications) may be subject to third-party terms. We are not
                    liable for third-party systems.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">8) Subscriptions, Billing & Taxes</h3>
                  <p className="text-sm sm:text-base">
                    Plans are prepaid and auto-renew unless cancelled. Prices exclude taxes unless stated.
                    You authorize us to charge applicable taxes. Refunds follow the Cancellations & Refunds
                    policy.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">9) Intellectual Property</h3>
                  <p className="text-sm sm:text-base">
                    We retain rights to the Service, documentation, algorithms, and models. You retain rights
                    to your Content and grant us a limited license to process it to provide the Service.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">10) Confidentiality</h3>
                  <p className="text-sm sm:text-base">
                    Each party will protect the other’s confidential information and use it only to deliver
                    or receive the Service.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">11) Service Levels & Changes</h3>
                  <p className="text-sm sm:text-base">
                    Target uptime: 99.9% monthly. We may improve or modify features; material changes will
                    be communicated with reasonable notice.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">12) Warranties & Disclaimers</h3>
                  <p className="text-sm sm:text-base">
                    The Service is provided “as is.” Implied warranties are disclaimed to the extent permitted
                    by law. We do not guarantee specific hiring outcomes.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">13) Limitation of Liability</h3>
                  <p className="text-sm sm:text-base">
                    To the maximum extent permitted by law, aggregate liability is limited to fees paid in
                    the 12 months before the claim. We are not liable for indirect or consequential damages
                    (including loss of profits, data, or business).
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">14) Indemnity</h3>
                  <p className="text-sm sm:text-base">
                    You will indemnify us for claims arising from unlawful use of the Service or violation of
                    these Terms.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">15) Suspension & Termination</h3>
                  <p className="text-sm sm:text-base">
                    We may suspend or terminate for breach, security risk, or non-payment. You may terminate
                    per your plan. Upon termination, data is retained or deleted per the Privacy Policy and
                    your settings.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">16) Governing Law; Dispute Resolution</h3>
                  <p className="text-sm sm:text-base">
                    Governed by the laws of India (Hyderabad), excluding conflict-of-law rules. Disputes are
                    resolved via confidential arbitration in Hyderabad, India, unless your enterprise contract
                    states otherwise.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">17) Changes to Terms</h3>
                  <p className="text-sm sm:text-base">
                    Updates will be posted with a revised “Last updated” date. Continued use constitutes
                    acceptance.
                  </p>
                </div>

                <div className="bg-primary/5 border-l-4 border-primary p-4 sm:p-6 rounded-r-lg space-y-2">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">18) Contact</h3>
                  <p className="text-sm sm:text-base">
                    <strong>VETRIAAI INNOVATION PRIVATE LIMITED</strong>
                  </p>
                  <p className="text-sm sm:text-base">
                    H. No. 5-95/1, Flat No. G, Mokkapati Towers, Fathenagar Colony,
                    K.v.rangareddy, Balanagar, Telangana, India, 500018
                  </p>
                  <p className="text-sm sm:text-base">
                    <strong>Email:</strong> <a href="mailto:info@vetriaai.com" className="text-primary hover:underline">info@vetriaai.com</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12 px-4 sm:px-6 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center text-gray-400">
            <p className="text-sm sm:text-base">© 2025 VetriaAI. All copyrights are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;
